import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import './Queue.scss';
import BrowserColumn from './BrowserColumn';
import QueueDetails from './QueueDetails/QueueDetails';
import { useCallback, useEffect, useRef, useState } from 'react';
import { QueuePanelProvider, QueuePanelSystem } from './common/QueuePanel';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ConversationPanel } from '../KSQ2/Panels/ConversationPanel';
import { BrowserPanel } from '../KSQ2/Panels/BrowserPanel';
import { FilterPanel } from '../KSQ2/Panels/FilterPanel';
import { useQueueContext } from './QueueContext';

type QueueProps = {
  /** Whether the queue is visible or hidden */
  visible?: boolean;
  /** Whether the queue should display in full screen mode */
  fullScreen?: boolean;
  /** Whether the left pane in the dashboard is visible */
  leftPaneVisible?: boolean;
  /** Function to toggle the left pane visibility */
  onToggleLeftPane?: (visible: boolean) => void;
};

/**
 * Queue component that displays the main interface for handling customer requests.
 * It consists of two main sections: a browser column for selecting requests and a
 * details panel for viewing and interacting with the selected request.
 *
 * @param {QueueProps} props - The component props
 * @param {boolean} [props.visible=true] - Whether the queue is visible or hidden
 * @param {boolean} [props.fullScreen=false] - Whether the queue should display in full screen mode
 * @returns {JSX.Element} The rendered Queue component
 */
const Queue = ({ visible = true, fullScreen = false, leftPaneVisible, onToggleLeftPane }: QueueProps): JSX.Element => {
  const prefix = prefixClass('queue');
  const [browserWidth, setBrowserWidth] = useState(330); // Default width from CSS
  const [isDragging, setIsDragging] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(330);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = browserWidth;
      setIsDragging(true);
    },
    [browserWidth]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      // Calculate width change based on mouse movement from starting point
      const deltaX = e.clientX - startXRef.current;
      const newWidth = startWidthRef.current + deltaX;

      // Check if we should collapse/expand the left pane based on resize thresholds
      if (leftPaneVisible && onToggleLeftPane && newWidth < 290) {
        // If dragging below minimum threshold and left pane is visible, collapse it
        onToggleLeftPane(false);
        setIsDragging(false); // Stop dragging after collapsing
        return;
      } else if (!leftPaneVisible && onToggleLeftPane && newWidth > 520) {
        // If dragging above maximum threshold and left pane is hidden, expand it
        onToggleLeftPane(true);
        setIsDragging(false); // Stop dragging after expanding
        return;
      }

      // Apply min/max constraints (310px min, 500px max)
      const constrainedWidth = Math.max(310, Math.min(newWidth, 500));

      setBrowserWidth(constrainedWidth);
    },
    [isDragging, leftPaneVisible, onToggleLeftPane]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <QueuePanelProvider>
      <QueueContent
        prefix={prefix}
        browserWidth={browserWidth}
        dividerRef={dividerRef}
        handleMouseDown={handleMouseDown}
        visible={visible}
        fullScreen={fullScreen}
      />
    </QueuePanelProvider>
  );
};

/**
 * Inner component that has access to QueueContext
 */
const QueueContent = ({
  prefix,
  browserWidth,
  dividerRef,
  handleMouseDown,
  visible,
  fullScreen,
}: {
  prefix: (suffix?: string) => string;
  browserWidth: number;
  dividerRef: React.RefObject<HTMLDivElement>;
  handleMouseDown: (e: React.MouseEvent) => void;
  visible: boolean;
  fullScreen: boolean;
}) => {
  const { enableQueueNewChat, enableQueueV2 } = useFlags();
  const USE_NEW_CONVERSATION_PANEL = enableQueueNewChat;
  // Feature flag for the new KSQ2 layout (FilterPanel + BrowserPanel + ConversationPanel)
  const USE_KSQ2_LAYOUT = enableQueueV2;
  const { state } = useQueueContext();
  const { selectedItem } = state;

  // KSQ2 Layout: FilterPanel (left) + BrowserPanel (middle) + ConversationPanel (right)
  if (USE_KSQ2_LAYOUT) {
    return (
      <div className={classNames(prefix(), prefix('ksq2'), { 'queue-visible': visible, 'full-screen': fullScreen })}>
        <div className={prefix('filter-panel')}>
          <FilterPanel />
        </div>
        <div className={prefix('browser-panel')} style={{ width: `${browserWidth}px` }}>
          <BrowserPanel />
        </div>
        <div
          className={prefix('divider')}
          ref={dividerRef}
          onMouseDown={handleMouseDown}
          style={{ left: `calc(200px + ${browserWidth}px)` }}
        />
        <div className={prefix('conversation-panel')}>
          {selectedItem ? (
            <ConversationPanel request={selectedItem} comments={selectedItem.comments} />
          ) : (
            <div className={prefix('empty-state')}>Select a request to view details</div>
          )}
        </div>
        <QueuePanelSystem />
      </div>
    );
  }

  // Legacy Layout: BrowserColumn + QueueDetails/ConversationPanel
  return (
    <div className={classNames(prefix(), { 'queue-visible': visible, 'full-screen': fullScreen })}>
      <div className={prefix('browser')} style={{ width: `${browserWidth}px` }}>
        <BrowserColumn />
      </div>
      <div
        className={prefix('divider')}
        ref={dividerRef}
        onMouseDown={handleMouseDown}
        style={{ left: `${browserWidth}px` }}
      />
      {USE_NEW_CONVERSATION_PANEL && selectedItem ? (
        <ConversationPanel request={selectedItem} comments={selectedItem.comments} />
      ) : (
        <QueueDetails />
      )}
      {/* Panel system mounted at Queue level for full-height panels */}
      <QueuePanelSystem />
    </div>
  );
};

export default Queue;
